import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useCookies } from "react-cookie";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import { format, isFuture, isToday, isValid, parseISO } from "date-fns";
import DirectionsWalkIcon from "@mui/icons-material/DirectionsWalk";
// @ts-ignore
import { useTheme } from "@mui/material/styles";
// hooks
import { useTransportLanguageRefresh } from "../../../common/hooks/useTransportLanguageRefresh";
// components
import AppDatePickerCustom from "../../../components/AppDatePickerCustom";
import { TransportRowAction, TransportRowAction2Buttons } from "../shared/transportRow";
import TransportProviderHeader from "../../../components/TransportProviderContent/TransportProviderHeader";
import LastMileInfo from "../../../components/LastMileInfo";
import AppButton from "../../../components/ui/AppButton";
import AppBookingError from "../../../components/AppBookingError";
import PaymentModal from "../../../components/Payment/PaymentModal";
import DeutscheBahnPassengersForm from "./DeutscheBahnPassengersForm";
import DeutscheBahnSummary from "./DeutscheBahnSummary";
// helpers
import { checkForEmptyNames } from "../shared/helpers/checkForEmptyNames";
import { resolveTransportStopCoords } from "../shared/helpers/resolveTransportStopCoords";
import { navigateBackOrToMain } from "../../../common/utils/helpers/navigateBackOrToMain";
import { pushTransportBookMarketplaceContext } from "../../../common/utils/analytics/pushTransportBookMarketplaceContext";
import {
  buildAirlineNavigationState,
  clearPersistedAirlineRouterState,
  persistAirlineFromNavigationState,
  readAirlineTransportAddressesFromNavigationState,
  resolveTransportAirlineApiContext
} from "../../../common/utils/transport/airlineTransportContext";
import { getDeutscheBahnOffer } from "./helpers/getDeutscheBahnOffer";
import { getDeutscheBahnOfferPrices } from "./helpers/deutscheBahnPassengerTypes";
import { getAirlineDeutscheBahnArrivalStation } from "./helpers/getAirlineDeutscheBahnArrivalStation";
import {
  DEFAULT_DEUTSCHE_BAHN_SERVICE_CLASS,
  DEUTSCHE_BAHN_CONFIRMATION_PATH,
  DEUTSCHE_BAHN_DISPLAY_NAME,
  DEUTSCHE_BAHN_PATH_SUFFIX,
  DEUTSCHE_BAHN_SERVICE_CLASS_OPTIONS,
  DEUTSCHE_BAHN_SLUG
} from "./constants";
import type { DeutscheBahnServiceClass } from "./types";
// actions
import { useActions } from "../../../common/hooks/useActions";
import { deutscheBahnActionCreators } from "./deutscheBahnModule";
import { errorFieldActionCreators } from "../../../common/store/modules/errorFieldModule";
// icons
import { dateIcon, serviceClassIcon } from "../../../common/vector";

export default function DeutscheBahn() {
  const theme = useTheme();
  const location = useLocation();
  const locationRef = useRef(location);
  locationRef.current = location;
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [cookies] = useCookies(["cookieAccepted"]);
  const language = localStorage.getItem("i18nextLng") || "en";
  const cssBlock = "deutsche-bahn";
  const dataAttrPrefix = DEUTSCHE_BAHN_SLUG;

  useLayoutEffect(() => {
    persistAirlineFromNavigationState(location.state);
  }, [location.state]);

  const restoredNavState = useMemo(() => buildAirlineNavigationState(location.state), [location.state]);

  const transportNavState = useMemo(() => restoredNavState ?? location.state, [restoredNavState, location.state]);

  const transportContext = useMemo(() => resolveTransportAirlineApiContext(transportNavState), [transportNavState]);
  const isAirlineTransferHub = transportContext?.mode === "airline";

  const transportAddresses = useMemo(
    () => readAirlineTransportAddressesFromNavigationState(transportNavState),
    [transportNavState]
  );

  const airlineSharedRoute = useMemo(() => {
    const nav = transportNavState as
      | {
          meta?: { source?: string };
          shared?: {
            departureDate?: string | null;
            milesAndMore?: string;
          };
        }
      | null
      | undefined;

    const isAirlineSource = nav?.meta?.source === "airline";

    return {
      ...transportAddresses,
      milesAndMore: isAirlineSource ? nav?.shared?.milesAndMore ?? "" : "",
      departureDate: isAirlineSource ? nav?.shared?.departureDate ?? null : null
    };
  }, [transportNavState, transportAddresses]);

  const [selectedDate, setSelectedDate] = useState(new Date());
  const [serviceClass, setServiceClass] = useState<DeutscheBahnServiceClass>(DEFAULT_DEUTSCHE_BAHN_SERVICE_CLASS);
  const [showValidationErrors, setShowValidationErrors] = useState(false);

  const tenantInfo = useSelector((state: any) => state.tenant.tenantInfo);
  const providerData = useSelector((state: any) => state.deutscheBahn.providerData);
  const customRouteCode = useSelector((state: any) => state.deutscheBahn.customRouteCode);
  const infoError = useSelector((state: any) => state.deutscheBahn.infoError);
  const passengerList = useSelector((state: any) => state.deutscheBahn.passengerList);
  const prebookPriceInfo = useSelector((state: any) => state.deutscheBahn.prebookPriceInfo);
  const errorPrebook = useSelector((state: any) => state.deutscheBahn.errorPrebook);
  const globalLoader = useSelector((state: any) => state.global.globalLoader);
  const platformType = useSelector((state: any) => state.global.platformType);
  const bookingResponseData = useSelector((state: any) => state.deutscheBahn.bookingResponseData);
  const bookingError = useSelector((state: any) => state.deutscheBahn.bookingError);
  const showPaymentModal = useSelector((state: any) => state.deutscheBahn.showPaymentModal);
  const lastMileRoute = useSelector((state: any) => state.deutscheBahn.lastMileRoute);
  const isLastMileRouteLoading = useSelector((state: any) => state.deutscheBahn.isLastMileRouteLoading);
  const lastMileRouteError = useSelector((state: any) => state.deutscheBahn.lastMileRouteError);

  const initDeutscheBahn = useActions(deutscheBahnActionCreators?.initDeutscheBahnAction, []);
  const getDeutscheBahnInfo = useActions(deutscheBahnActionCreators?.getDeutscheBahnInfoAction, []);
  const getPricePrebook = useActions(deutscheBahnActionCreators?.getPriceDeutscheBahnPrebookAction, []);
  const getLastMileRoute = useActions(deutscheBahnActionCreators?.getDeutscheBahnLastMileRouteAction, []);
  const resetPrebookDataInfo = useActions(deutscheBahnActionCreators?.resetPrebookDataInfoAction, []);
  const buyTicket = useActions(deutscheBahnActionCreators?.buyDeutscheBahnTicketAction, []);
  const togglePaymentModal = useActions(deutscheBahnActionCreators?.togglePaymentModalAction, []);
  const toggleBookingError = useActions(deutscheBahnActionCreators?.toggleBookingErrorAction, []);
  const resetState = useActions(deutscheBahnActionCreators?.resetDeutscheBahnStateAction, []);
  const resetErrorField = useActions(errorFieldActionCreators?.resetErrorFieldAction, []);

  useEffect(() => {
    initDeutscheBahn();
  }, [initDeutscheBahn]);

  useEffect(() => {
    return () => {
      togglePaymentModal(false);
      resetState();
      resetErrorField();
      clearPersistedAirlineRouterState();
    };
  }, [togglePaymentModal, resetState, resetErrorField]);

  useEffect(() => {
    if (tenantInfo) {
      getDeutscheBahnInfo(transportContext);
    }
  }, [tenantInfo, transportContext, getDeutscheBahnInfo]);

  useEffect(() => {
    if (!airlineSharedRoute.departureDate) {
      return;
    }

    const parsedDate = parseISO(airlineSharedRoute.departureDate);
    if (isValid(parsedDate)) {
      setSelectedDate(parsedDate);
    }
  }, [airlineSharedRoute.departureDate]);

  useTransportLanguageRefresh({
    language,
    navigate,
    pathSuffix: DEUTSCHE_BAHN_PATH_SUFFIX,
    getNavigationState: () => buildAirlineNavigationState(locationRef.current.state),
    iataCode: transportContext.mode === "airline" ? transportContext.iataCode : null
  });

  // The arrival station is picked server-side. The airline search already returned it, so prefer that;
  // fall back to the estimation response for direct/hotel entries into the page.
  const airlineArrivalStation = useMemo(
    () => getAirlineDeutscheBahnArrivalStation(transportNavState),
    [transportNavState]
  );
  const arrivalStation = airlineArrivalStation ?? prebookPriceInfo?.details?.destination;
  const destinationPlaceId = airlineSharedRoute.destinationAddressId;
  const finalDestinationName = airlineSharedRoute.destinationAddress;
  const arrivalStationName = arrivalStation?.name ?? null;
  const arrivalStationCoords = useMemo(() => resolveTransportStopCoords(arrivalStation), [arrivalStation]);

  useEffect(() => {
    if (!isAirlineTransferHub || !destinationPlaceId || arrivalStationCoords === null) {
      return;
    }

    getLastMileRoute({
      departureLatitude: arrivalStationCoords.lat,
      departureLongitude: arrivalStationCoords.lng,
      destinationPlaceId,
      travelMode: "walking"
    });
  }, [isAirlineTransferHub, destinationPlaceId, arrivalStationCoords, getLastMileRoute]);

  const offer = useMemo(() => getDeutscheBahnOffer(providerData), [providerData]);
  const offerPrices = useMemo(() => getDeutscheBahnOfferPrices(offer), [offer]);
  // Without offer prices there are no passenger types to send, so the estimation cannot work.
  const isAvailable = Boolean(customRouteCode && offer && offerPrices.length > 0) && !infoError;
  const [firstClassOption, secondClassOption] = DEUTSCHE_BAHN_SERVICE_CLASS_OPTIONS;

  const handleServiceClassChange = (value: DeutscheBahnServiceClass) => {
    if (value === serviceClass) {
      return;
    }
    setServiceClass(value);
    resetPrebookDataInfo();
  };

  const isDateValid = useCallback((): boolean => isToday(selectedDate) || isFuture(selectedDate), [selectedDate]);

  const handleSeePriceBtn = () => {
    setShowValidationErrors(true);

    if (!isDateValid() || checkForEmptyNames(passengerList)) {
      return;
    }

    getPricePrebook(
      {
        start_date: format(selectedDate, "yyyy-MM-dd"),
        offer_code: customRouteCode,
        qualityOfService: serviceClass,
        milesAndMore: airlineSharedRoute.milesAndMore,
        departureAddress: airlineSharedRoute.departureAddress,
        departureAddressId: airlineSharedRoute.departureAddressId,
        destinationAddress: airlineSharedRoute.destinationAddress,
        destinationAddressId: airlineSharedRoute.destinationAddressId
      },
      transportContext
    );
  };

  const handleBuyTicketBtn = () => {
    pushTransportBookMarketplaceContext({
      cookieAccepted: cookies.cookieAccepted,
      platformType,
      tenantInfo,
      offerCode: customRouteCode ?? null
    });

    buyTicket(
      {
        start_date: Math.round(+new Date(selectedDate) / 1000),
        offer_code: customRouteCode
      },
      navigate,
      transportContext
    );
  };

  const renderPrebookErrorMsg = () => {
    if (isDateValid() && !errorPrebook) {
      return null;
    }

    let errors: Array<any> = [];
    if (!isDateValid()) {
      errors.push({ errorMessage: t("transport.timeErrorMsg") });
    }
    if (errorPrebook?.fields) {
      errors = errors.concat(errorPrebook.fields);
    }
    if (!errors.length) {
      errors.push({ errorMessage: t("transport.prebookCustomError") });
    }

    return errors.map((error: any, i: number) => (
      <div key={i} className={`${cssBlock}__error-msg`}>
        {error?.errorMessage}
      </div>
    ));
  };

  const getDisabledStyleForTicketsForm = () => {
    if (prebookPriceInfo) {
      return { opacity: 0.4, pointerEvents: "none" as const };
    }
  };

  return (
    <Grid
      className={`${cssBlock} grid-box`}
      item
      sx={{ flexDirection: "column", flex: 1, margin: 0, padding: 0 }}
      mt={{ xs: 1, sm: 3 }}
      mb={{ xs: 4, sm: 5 }}
    >
      {bookingError ? (
        <AppBookingError
          dataAttribute={`${dataAttrPrefix}-booking-error-button`}
          onButtonClick={() => toggleBookingError(false)}
          title={t("bookingForm.error.title")}
          subtitle={t("bookingForm.error.message")}
        />
      ) : (
        <>
          <TransportProviderHeader
            title={providerData?.provider_title ?? providerData?.title ?? DEUTSCHE_BAHN_DISPLAY_NAME}
            shortDescription={providerData?.provider_description}
            providerLogo={providerData?.provider_logo}
            titleClassName={`${cssBlock}__title`}
          />

          {isAvailable ? (
            <Grid container>
              <Grid item md={6} xs={12} sx={{ display: "flex", flexDirection: "column" }}>
                <Box sx={{ ...getDisabledStyleForTicketsForm() }}>
                  <TransportRowAction
                    label={t("transport.departureDate")}
                    icon={dateIcon?.default}
                    actionComponent={
                      <Grid item width={{ xs: 200, sm: 220, md: 260 }}>
                        <AppDatePickerCustom
                          selectedDate={selectedDate}
                          setSelectedDate={date => date != null && setSelectedDate(date)}
                          minDate={new Date()}
                        />
                      </Grid>
                    }
                  />
                  <TransportRowAction
                    label={t("transport.serviceClass")}
                    icon={serviceClassIcon?.default}
                    actionComponent={
                      <TransportRowAction2Buttons
                        firstButtonLabel={t(firstClassOption.labelKey)}
                        firstButtonHandle={() => handleServiceClassChange(firstClassOption.value)}
                        secondButtonLabel={t(secondClassOption.labelKey)}
                        secondButtonHandle={() => handleServiceClassChange(secondClassOption.value)}
                        isFirstButtonActive={serviceClass === firstClassOption.value}
                      />
                    }
                  />
                </Box>
                {isAirlineTransferHub && finalDestinationName ? (
                  <Box mt={{ xs: 3, sm: 4 }} pl={{ xs: 2, sm: 4 }}>
                    <LastMileInfo
                      title={t("transport.lastMile.title")}
                      icon={<DirectionsWalkIcon fontSize="small" />}
                      destinationName={finalDestinationName}
                      stopName={arrivalStationName}
                      distanceMeters={lastMileRoute?.distance_meters ?? null}
                      durationSeconds={lastMileRoute?.duration_seconds ?? null}
                      googleMapsUrl={lastMileRoute?.google_maps_url ?? null}
                      isLoading={isLastMileRouteLoading}
                      error={lastMileRouteError}
                      mapsLinkBelow
                    />
                  </Box>
                ) : null}
              </Grid>
              <Grid
                item
                md={6}
                xs={12}
                mt={{ sm: 6, xs: 6, md: 0 }}
                sx={{ ...getDisabledStyleForTicketsForm() }}
                position={"relative"}
              >
                <div className="vertical-divider" />
                <Box pl={{ xs: 2, sm: 6 }} pr={{ xs: 2, sm: 4 }}>
                  <DeutscheBahnPassengersForm offerPrices={offerPrices} showValidationErrors={showValidationErrors} />
                  {renderPrebookErrorMsg()}
                  <Grid
                    container={isAirlineTransferHub}
                    item
                    xs={12}
                    sm={isAirlineTransferHub ? undefined : 4}
                    sx={{
                      ...(isAirlineTransferHub ? { display: "flex", alignItems: "stretch", gap: 2 } : {})
                    }}
                  >
                    {isAirlineTransferHub && (
                      <Grid item sx={{ flex: 1 }}>
                        <AppButton
                          label={t("transport.goBack")}
                          onClick={() => navigateBackOrToMain(navigate, { language })}
                          disableRipple={true}
                          customBackgroundColor={theme.palette.primary.dark}
                          customHoverColor={theme.palette.primary.dark}
                          fontSize="14px"
                          dataAttribute={`${dataAttrPrefix}-go-back`}
                        />
                      </Grid>
                    )}

                    <Grid item sx={{ ...(isAirlineTransferHub ? { flex: 1 } : {}) }}>
                      <AppButton
                        label={t("transport.seePriceBtn")}
                        onClick={handleSeePriceBtn}
                        disableRipple={true}
                        customBackgroundColor={tenantInfo?.style?.button?.color}
                        customActiveColor={tenantInfo?.style?.button?.active}
                        customHoverColor={tenantInfo?.style?.button?.hover}
                        customClickColor={tenantInfo?.style?.button?.click}
                        fontSize="14px"
                        dataAttribute={`${dataAttrPrefix}-see-price`}
                      />
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
              <Grid item xs={12} mt={{ xs: 4, sm: 6 }} ml={{ xs: 2, sm: 5, md: 6 }} mr={{ xs: 2, sm: 5, md: 6 }}>
                {prebookPriceInfo && (
                  <DeutscheBahnSummary
                    offerName={offer?.name}
                    offerPrices={offerPrices}
                    onBuyTicket={handleBuyTicketBtn}
                    cancellationPolicy={offer?.cancellation_policy || providerData?.cancellation_policy}
                  />
                )}
              </Grid>
            </Grid>
          ) : (
            !globalLoader && (
              <Grid item xs={12} pb={4}>
                <Box sx={{ fontSize: 18 }}>{t("transport.notAvailable")}</Box>
              </Grid>
            )
          )}
        </>
      )}
      {showPaymentModal && (
        <PaymentModal
          isOpen={showPaymentModal}
          bookingResponseData={bookingResponseData}
          togglePaymentModal={togglePaymentModal}
          redirectUrl={DEUTSCHE_BAHN_CONFIRMATION_PATH}
        />
      )}
    </Grid>
  );
}
